import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useDonationDetailsMicrosite } from '@hooks/register/screening/use-donation-details-microsite';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { donationDetailsMicrositeSchema } from '@views/register/screening/donation-details-microsite.schema';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { IDonationDetailsMicrositeFormValues, IDonationDetailsMicrositeModel, IDonationDetailsMicrositeSubmitResult } from '@/types/screening/donation-details-microsite';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

const DonationDetailsMicrosite = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useDonationDetailsMicrosite(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <DonationDetailsMicrositeContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IDonationDetailsMicrositeContentProps {
    model: IDonationDetailsMicrositeModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IDonationDetailsMicrositeFormValues) => Promise<IDonationDetailsMicrositeSubmitResult>;
}

const asString = (value: number | boolean | null) => (value != null ? String(value) : '');

const DonationDetailsMicrositeContent = ({ model, isSubmitting, submitError, onSubmit }: IDonationDetailsMicrositeContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IDonationDetailsMicrositeFormValues, IDonationDetailsMicrositeSubmitResult>({
        resolver: zodResolver(donationDetailsMicrositeSchema),
        defaultValues: {
            ddRelationshipToPatient: asString(model.ddRelationshipToPatient),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        formState: { errors },
    } = form;

    return (
        <StepFormLayout
            kind="screening"
            model={model}
            step={DashRegistrationStep.DonationDetailsMicrosite}
            title={t('DashLabel_DonationDetails', 'Your Donation')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t(
                        'DashLabel_WhatTransplantCenterIsTheIntendedRecipientAt',
                        'What transplant center is the intended recipient at',
                    )}
                    ?
                </label>
                <div className="col-xs-12 col-sm-8" style={{ marginLeft: 10 }}>
                    <div className="form-control-static">
                        {model.recipientCenterName}
                    </div>
                </div>
            </div>

            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t("DashLabel_RecipientSFirstName", "Recipient's First Name")}:
                </label>
                <div className="col-xs-12 col-sm-8" style={{ marginLeft: 10 }}>
                    <div className="form-control-static">{model.ddRecipFirstName}</div>
                </div>
            </div>

            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t("DashLabel_RecipientSLastName", "Recipient's Last Name")}:
                </label>
                <div className="col-xs-12 col-sm-8" style={{ marginLeft: 10 }}>
                    <div className="form-control-static">{model.ddRecipLastName}</div>
                </div>
            </div>

            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t('DashLabel_RecipientsAge', "Recipient's Age")}:
                </label>
                <div className="col-xs-12 col-sm-8" style={{ marginLeft: 10 }}>
                    <div className="form-control-static">{model.recipientAgeText}</div>
                </div>
            </div>

            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t(
                        'DashLabel_HowDoYouKnowYourIntendedRecipient',
                        'What is your relationship with your intended recipient',
                    )}{' '}
                    ({model.micrositeRecipientFullName}) ?
                    <FieldError message={errors.ddRelationshipToPatient?.message} />
                </label>
                <div className="col-xs-12 col-sm-8" style={{ marginLeft: 10 }}>
                    <select
                        className="form-control"
                        {...register('ddRelationshipToPatient')}
                    >
                        <option value="">
                            {t('DashLabel_PleaseSelect', 'Please Select')}
                        </option>
                        {model.relationshipOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="form-group" style={{ marginTop: 30 }}></div>
        </StepFormLayout>
    );
};

export default DonationDetailsMicrosite;
